import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/site/SEO";
import { Download, FileText, Sparkles, Feather, Mic, BookOpen, Search, ArrowRight, Mail, Check } from "lucide-react";
import { toast } from "sonner";

/** Map content_type slug → pretty label + tint colors. */
const CONTENT_TYPE_MAP = {
  editorial: { label: "Editorial",  fg: "#5B21B6", bg: "#EDE9FE", icon: Feather },
  insight:   { label: "Insight",    fg: "#1E3A8A", bg: "#DBEAFE", icon: Sparkles },
  laporan:   { label: "Laporan",    fg: "#BE5730", bg: "#F6E7DF", icon: FileText },
  wawancara: { label: "Wawancara",  fg: "#3B6D46", bg: "#E4EFE6", icon: Mic },
  feature:   { label: "Feature",    fg: "#8A5A00", bg: "#FFF8E6", icon: BookOpen },
};

const FILTERS = ["all", "editorial", "insight", "laporan", "wawancara", "feature"];

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
}

function ContentTypeBadge({ type, testid, size = "md" }) {
  const meta = CONTENT_TYPE_MAP[type] || CONTENT_TYPE_MAP.insight;
  const Icon = meta.icon;
  const cls = size === "sm"
    ? "text-[10px] px-1.5 py-0.5 gap-1"
    : "text-[11px] px-2 py-0.5 gap-1.5";
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-full font-bold uppercase tracking-widest ${cls}`}
      style={{ background: meta.bg, color: meta.fg }}
    >
      <Icon size={size === "sm" ? 10 : 12} strokeWidth={2.25} />
      {meta.label}
    </span>
  );
}

function AuthorLine({ post, size = "md" }) {
  const avatar = post.author_avatar_url;
  const title = post.author_title;
  return (
    <div className="flex items-center gap-2.5" data-testid={`journal-author-${post.post_id}`}>
      {avatar ? (
        <img src={avatar} alt={post.author_name}
          className={size === "lg" ? "w-11 h-11 rounded-full object-cover" : "w-8 h-8 rounded-full object-cover"} />
      ) : (
        <div className={(size === "lg" ? "w-11 h-11 text-sm " : "w-8 h-8 text-xs ") +
          "rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] font-bold flex items-center justify-center"}>
          {(post.author_name || "").split(" ").map((w) => w[0]).slice(0, 2).join("")}
        </div>
      )}
      <div className="min-w-0">
        <div className={(size === "lg" ? "text-sm " : "text-xs ") + "font-semibold text-[#1F2937] truncate"}>
          {post.author_name || "Redaksi"}
        </div>
        {title && (
          <div className={(size === "lg" ? "text-[11px] " : "text-[10px] ") + "text-[#8A8F99] uppercase tracking-widest truncate"}>
            {title}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Blog() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  // Cross-discovery: `/?tag=xxx` (legacy `/jurnal?tag=xxx` 301s here) deep-links directly into the tag filter,
  // driven by the coral tag pills on the article footer + forum thread cards.
  const [params, setParams] = useSearchParams();
  const tagParam = params.get("tag") || "";

  useEffect(() => {
    api.get("/blog").then((r) => setItems(r.data || []));
  }, []);

  const featured = useMemo(() => items.find((p) => p.is_featured) || null, [items]);
  const rest = useMemo(() => items.filter((p) => p.post_id !== featured?.post_id), [items, featured]);

  const filtered = useMemo(() => {
    let list = rest;
    if (filter !== "all") list = list.filter((p) => (p.content_type || "insight") === filter);
    if (tagParam) {
      const t = tagParam.toLowerCase();
      list = list.filter((p) => (p.tags || []).some((x) => x.toLowerCase() === t));
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((p) =>
        (p.title || "").toLowerCase().includes(needle) ||
        (p.excerpt || "").toLowerCase().includes(needle) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(needle))
      );
    }
    return list;
  }, [rest, filter, q, tagParam]);

  return (
    <div data-testid="blog-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      {/* Listing-level SEO — declarative via Helmet (Sep 2026). Previously an
          imperative `document.title = …` which overwrote the prerender
          snapshot's <title> after hydration, producing divergent titles
          between initial HTML and crawler-rendered DOM. Values here MUST match
          scripts/journalSeoRenderer.js renderListingHtml. */}
      <SEO
        title="The Reports — Wawasan Pendidikan untuk Orang Tua"
        description="Panduan, analisis, wawancara, dan cerita untuk membantu orang tua membuat keputusan pendidikan dengan lebih jernih."
        image="/brand/the-reports-og.png"
        canonical={`${typeof window !== "undefined" ? window.location.origin : "https://theschoolreports.id"}/`}
      />
      {/* Journal masthead */}
      <div className="border-b border-[#E8E4DB] pb-8">
        <div className="eyebrow" data-testid="journal-eyebrow">THE REPORTS</div>
        <h1 className="headline mt-3" data-testid="journal-page-title">
          Wawasan pendidikan yang membantu orang tua berpikir lebih jernih.
        </h1>
        <p className="mt-3 text-[#5C6472] max-w-2xl leading-relaxed">
          Editorial, riset & analisis mendalam, dan cerita panjang untuk membantu orang tua
          merencanakan masa depan anak yang lebih bijaksana.
        </p>
      </div>

      {/* Featured "Laporan Utama" hero */}
      {featured && (
        <FeaturedHero post={featured} />
      )}

      {/* Filter chips + search */}
      <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 sticky top-16 z-10 bg-[#F9F8F6] py-3">
        <div className="flex flex-wrap gap-2" data-testid="journal-filters">
          {FILTERS.map((f) => {
            const meta = f === "all" ? { label: "Semua" } : CONTENT_TYPE_MAP[f];
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                data-testid={`journal-filter-${f}`}
                className={
                  "px-4 py-1.5 rounded-full text-sm border transition-colors whitespace-nowrap " +
                  (filter === f
                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                    : "bg-white text-[#5C6472] border-[#E8E4DB] hover:bg-[#F3F1ED]")
                }
              >
                {meta.label}
              </button>
            );
          })}
          {tagParam && (
            <button
              type="button"
              onClick={() => { params.delete("tag"); setParams(params, { replace: true }); }}
              data-testid="journal-active-tag-pill"
              title="Klik untuk menghapus filter tag"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-[#D2673E] bg-[#FDF6F0] text-[#BE5730] hover:bg-[#F6E7DF] transition-colors"
            >
              <span aria-hidden="true">#</span>{tagParam}<span aria-hidden="true" className="opacity-60">×</span>
            </button>
          )}
        </div>
        <div className="relative w-full md:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F99]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari laporan / riset…"
            data-testid="journal-search"
            className="w-full h-10 pl-9 pr-3 rounded-full bg-white border border-[#E8E4DB] focus:border-[#D2673E] focus:outline-none text-sm"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((p, i) => (
          <ArticleCard key={p.post_id} post={p} testid={`blog-card-${i}`} index={i} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full py-12 text-center text-[#8A8F99] italic" data-testid="journal-empty">
            Belum ada tulisan untuk filter ini.
          </div>
        )}
      </div>

      {/* Newsletter subscribe — bottom of Journal */}
      <NewsletterCTA />
    </div>
  );
}

function NewsletterCTA() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | already
  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("Alamat email tidak valid");
      return;
    }
    setStatus("loading");
    try {
      const r = await api.post("/newsletter/subscribe", { email: v, source: "journal" });
      setStatus(r.data?.already_subscribed ? "already" : "ok");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal berlangganan");
      setStatus("idle");
    }
  };
  return (
    <section
      data-testid="journal-newsletter"
      className="mt-16 mb-4 rounded-3xl border border-[#E8E4DB] bg-gradient-to-br from-[#F6E7DF]/60 via-white to-[#F9F8F6] p-8 lg:p-12 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center overflow-hidden relative"
    >
      <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#D2673E]/10 blur-3xl" aria-hidden="true" />
      <div>
        <div className="eyebrow">Laporan bulanan</div>
        <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1B2B4D] mt-3 leading-tight">
          Terima laporan baru <em className="text-[#D2673E] not-italic">langsung</em> ke inbox Anda
        </h2>
        <p className="mt-3 text-[#5C6472] leading-relaxed max-w-lg">
          Satu email sebulan berisi ringkasan riset terbaru, analisis panjang, dan tren pendidikan Jabodetabek — kurasi tim redaksi kami. Bebas biaya. Berhenti kapan saja.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3" data-testid="journal-newsletter-form">
        <label className="block text-[11px] uppercase tracking-widest font-bold text-[#1E3A8A]">Alamat email</label>
        <div className="relative">
          <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8F99]" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (status !== "idle") setStatus("idle"); }}
            placeholder="Masukan Email Anda"
            data-testid="journal-newsletter-email"
            disabled={status === "loading"}
            className="w-full h-12 pl-11 pr-4 rounded-full bg-white border border-[#E8E4DB] focus:border-[#D2673E] focus:ring-2 focus:ring-[#D2673E]/20 focus:outline-none text-sm placeholder-[#8A8F99] transition-all disabled:opacity-60"
          />
        </div>
        <button
          type="submit"
          disabled={status === "loading" || status === "ok" || status === "already"}
          data-testid="journal-newsletter-submit"
          className="w-full h-12 rounded-full bg-[#D2673E] hover:bg-[#BE5730] active:bg-[#A8481C] disabled:opacity-60 text-white text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2"
        >
          {status === "loading" && "Mendaftarkan…"}
          {status === "ok" && (<><Check size={16} /> Terima kasih! Cek email Anda</>)}
          {status === "already" && (<><Check size={16} /> Anda sudah terdaftar</>)}
          {status === "idle" && "Berlangganan gratis"}
        </button>
        <p className="text-[11px] text-[#8A8F99] leading-snug">
          Dengan berlangganan, Anda menyetujui menerima email dari The School Reports. Kami tidak pernah menjual data.
        </p>
      </form>
    </section>
  );
}

function FeaturedHero({ post }) {
  const meta = CONTENT_TYPE_MAP[post.content_type] || CONTENT_TYPE_MAP.insight;
  const stats = (post.key_stats || []).slice(0, 4);
  const isPdf = post.content_type === "laporan" && post.pdf_url;
  return (
    <Link
      to={`/jurnal/${post.slug}`}
      data-testid="journal-featured"
      className="group mt-10 block relative rounded-3xl overflow-hidden border border-[#E8E4DB] bg-white hover:shadow-[0_20px_50px_rgba(30,58,138,0.10)] transition-shadow"
    >
      <div className="grid lg:grid-cols-2">
        {/* Left: text + stats */}
        <div className="p-8 lg:p-12 flex flex-col">
          <div className="flex items-center gap-2">
            <span className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#1E3A8A] text-white">
              Laporan Utama
            </span>
            <ContentTypeBadge type={post.content_type} testid="journal-featured-type" />
          </div>
          <h2 className="headline-card mt-4 group-hover:text-[#D2673E] transition-colors">
            {post.title}
          </h2>
          <p className="mt-3 text-[#5C6472] leading-relaxed line-clamp-3">{post.excerpt}</p>

          {stats.length > 0 && (
            <div className="mt-6 grid grid-cols-2 gap-3" data-testid="journal-featured-stats">
              {stats.map((s, i) => (
                <div key={i} className="p-4 rounded-2xl bg-[#F6E7DF]/40 border border-[#F6E7DF]"
                  data-testid={`journal-featured-stat-${i}`}>
                  <div className="font-serif-display text-3xl lg:text-4xl text-[#D2673E] leading-none">
                    {s.value}{s.unit ? <span className="text-lg">{s.unit}</span> : null}
                  </div>
                  <div className="mt-1.5 text-[11px] text-[#5C6472] leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-auto pt-6 flex items-center justify-between gap-4 flex-wrap">
            <AuthorLine post={post} size="lg" />
            <div className="text-xs text-[#8A8F99]">
              {formatDate(post.created_at)}
              {post.read_time_min ? ` · ${post.read_time_min} mnt baca` : ""}
            </div>
          </div>

          {isPdf && (
            <div className="mt-4 flex items-center gap-3 pt-4 border-t border-[#E8E4DB]">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(post.pdf_url, "_blank"); }}
                data-testid="journal-featured-pdf"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#D2673E] hover:bg-[#BE5730] text-white text-sm font-semibold transition-colors"
              >
                <Download size={16} /> Unduh laporan PDF
              </button>
              {post.pdf_size_bytes && (
                <span className="text-xs text-[#8A8F99]">
                  {(post.pdf_size_bytes / (1024 * 1024)).toFixed(1)} MB
                </span>
              )}
            </div>
          )}
        </div>
        {/* Right: hero image */}
        {post.cover_url ? (
          <div className="hidden lg:block relative overflow-hidden bg-[#F3F1ED]">
            <img src={post.cover_url} alt={post.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </div>
        ) : (
          <div className="hidden lg:flex items-center justify-center bg-gradient-to-br from-[#1E3A8A] to-[#D2673E]">
            <FileText size={64} className="text-white/40" />
          </div>
        )}
      </div>
    </Link>
  );
}

function ArticleCard({ post, testid, index }) {
  const isPdf = post.content_type === "laporan" && post.pdf_url;
  return (
    <Link
      to={`/jurnal/${post.slug}`}
      data-testid={testid}
      className="group animate-fade-up bg-white border border-[#E8E4DB] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(30,58,138,0.06)] transition-all duration-300 flex flex-col"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {post.cover_url ? (
        <div className="aspect-[16/10] overflow-hidden bg-[#F3F1ED] relative">
          <img src={post.cover_url} alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute top-3 left-3">
            <ContentTypeBadge type={post.content_type} testid={`${testid}-type`} size="sm" />
          </div>
        </div>
      ) : (
        <div className="p-4 border-b border-[#E8E4DB]">
          <ContentTypeBadge type={post.content_type} testid={`${testid}-type`} size="sm" />
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="headline-grid group-hover:text-[#D2673E] transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="mt-2 text-sm text-[#5C6472] line-clamp-3 leading-relaxed">{post.excerpt}</p>
        {/* Stats mini strip (only if this card has featured stats and isn't hero) */}
        {(post.key_stats || []).length > 0 && (
          <div className="mt-3 flex gap-3 text-[#D2673E]" data-testid={`${testid}-stats`}>
            {post.key_stats.slice(0, 2).map((s, i) => (
              <div key={i} className="flex-1 min-w-0">
                <div className="font-serif-display text-lg leading-none">{s.value}{s.unit || ""}</div>
                <div className="text-[10px] text-[#8A8F99] truncate">{s.label}</div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-auto pt-4 flex items-center justify-between gap-3">
          <AuthorLine post={post} />
          <div className="text-[11px] text-[#8A8F99] whitespace-nowrap">
            {post.read_time_min ? `${post.read_time_min} mnt` : formatDate(post.created_at)}
          </div>
        </div>
        {isPdf && (
          <div className="mt-3 pt-3 border-t border-[#E8E4DB] flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#BE5730]">
              <Download size={13} /> PDF tersedia
            </span>
            {post.pdf_size_bytes && (
              <span className="text-[10px] text-[#8A8F99]">{(post.pdf_size_bytes / (1024 * 1024)).toFixed(1)} MB</span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
